import React, { useEffect, useState } from "react";
import { getTodayIST } from "../../utils/dateUtils";
import Loader from "./Loader";

export default function TokenDashboard({
  tokens = [],
  patients = [],
  treatments = [], // treatment_entries
  packagePayments = [],
  packages = [],
}) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (
      tokens.length ||
      treatments.length ||
      packagePayments.length ||
      packages.length
    ) {
      setLoading(false);
    }
  }, [tokens, treatments, packagePayments, packages]);
  // useEffect(() => {
  // console.log("Tokens Updated:", tokens.length);
  // console.log("Today Tokens:", tokensToday.length);
// }, [tokens]);

  if (loading) {
    return <Loader text="Fetching dashboard data..." />;
  }

  const todayDate = getTodayIST();

  // =========================
  // DATE RANGE
  // =========================
 const startOfDay = new Date();
startOfDay.setHours(0, 0, 0, 0);

const endOfDay = new Date();
endOfDay.setHours(23, 59, 59, 999);

  // =========================
  // TOKENS FILTER
  // =========================
  const tokensToday = tokens.filter(
  (t) => t.visit_date === todayDate
);
console.log("todayDate", todayDate);
console.log("last token date", tokens[tokens.length - 1]?.visit_date);
  // =========================
  // TREATMENTS
  // =========================
  const treatmentsToday = treatments.filter((tr) => {
    if (!tr.created_at) return false;

    const d = tr.created_at.toDate
      ? tr.created_at.toDate()
      : new Date(tr.created_at);

    return d >= startOfDay && d <= endOfDay;
  });

  const treatmentTotal = treatmentsToday.reduce(
    (sum, t) => sum + Number(t.amount || 0),
    0
  );

  // =========================
  // PACKAGE PAYMENTS
  // =========================
  const packageToday = packagePayments.filter((p) => {
    if (!p.created_at) return false;

    const d = p.created_at.toDate
      ? p.created_at.toDate()
      : new Date(p.created_at);

    return d >= startOfDay && d <= endOfDay;
  });

  const packageTotal = packageToday.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );

  // =========================
  // CONSULTATION
  // =========================
  const consultationTotal = tokensToday 
    .filter((t) => t.consultation_payment_status === "PAID")
    .reduce(
      (sum, t) => sum + Number(t.consultation_fee || 0),
      0
    );

  // =========================
  // UNPAID
  // =========================
  const consultationUnpaid = tokensToday .filter(
    (t) =>
      t.visit_category === "CONSULTATION" &&
      t.consultation_payment_status === "UNPAID"
  ).length;

  const treatmentUnpaid = tokensToday .filter(
    (t) =>
      t.visit_category === "TREATMENT" &&
      t.treatment_payment_status !== "PAID"
  ).length;

  const packagesToday = packages.filter((pkg) => {
    if (!pkg.start_date) return false;

    const d = pkg.start_date.toDate
      ? pkg.start_date.toDate()
      : new Date(pkg.start_date);

    return d >= startOfDay && d <= endOfDay;
  });

  const packageUnpaid = packagesToday.filter((pkg) => {
    const due = Number(pkg.due_amount || 0);
    const paid = Number(pkg.paid_amount || 0);
    return due > 0 && paid > 0;
  }).length;

  const unpaidTodayCount =
    consultationUnpaid + treatmentUnpaid + packageUnpaid;

  const totalAmountToday =
    consultationTotal + treatmentTotal + packageTotal;

  // =========================
  // BRANCH MAP
  // =========================
  const branchMap = {};

  const ensureBranch = (branch) => {
    if (!branchMap[branch]) {
      branchMap[branch] = {
        total: 0,
        paid: 0,
        unpaid: 0,
        package: 0,
        amount: 0,
        treatmentAmount: 0,
        treatmentCount: 0,
        packageAmount: 0,
      };
    }
  };

  tokensToday .forEach((t) => {
    const branch = t.branch_name || "Unknown Branch";
    ensureBranch(branch);

    branchMap[branch].total += 1;

    if (
      t.visit_category === "CONSULTATION" &&
      t.consultation_payment_status === "PAID"
    ) {
      branchMap[branch].paid += 1;
      branchMap[branch].amount += Number(t.consultation_fee || 0);
    }

    if (
      t.visit_category === "CONSULTATION" &&
      t.consultation_payment_status === "UNPAID"
    ) {
      branchMap[branch].unpaid += 1;
    }

    if (
      t.visit_category === "TREATMENT" &&
      t.treatment_payment_status !== "PAID"
    ) {
      branchMap[branch].unpaid += 1;
    }
  });

  treatmentsToday.forEach((tr) => {
    const branch = tr.branch_name || "Unknown Branch";
    ensureBranch(branch);

    branchMap[branch].treatmentCount += 1;
    branchMap[branch].treatmentAmount += Number(tr.amount || 0);
  });

  const branchPackageMap = {};

  packageToday.forEach((p) => {
    const branch = p.branch_name || "Unknown Branch";
    ensureBranch(branch);

    if (!branchPackageMap[branch]) {
      branchPackageMap[branch] = new Set();
    }

    branchPackageMap[branch].add(p.package_id);

    branchMap[branch].packageAmount += Number(p.amount || 0);
  });

  Object.keys(branchPackageMap).forEach((branch) => {
    branchMap[branch].package = branchPackageMap[branch].size;
  });

  packagesToday.forEach((pkg) => {
    const branch = pkg.branch_name || "Unknown Branch";
    ensureBranch(branch);

    const due = Number(pkg.due_amount || 0);
    const paid = Number(pkg.paid_amount || 0);

    if (due > 0 && paid > 0) {
      branchMap[branch].unpaid += 1;
    }
  });

  const branchStats = Object.entries(branchMap).map(
    ([branch, data]) => ({
      branch,
      ...data,
    })
  );

  // =========================
  // UI STATS (OLD STYLE SAME)
  // =========================
  const tokenStats = [
    {
      label: "Tokens Today",
      color: "bg-blue-100 text-blue-800",
      count: tokensToday .length,
    },
    {
      label: "Total Amount ₹",
      color: "bg-yellow-100 text-yellow-800",
      count: totalAmountToday,
    },
    {
      label: "Paid Patients",
      color: "bg-green-100 text-green-800",
      count: tokensToday .filter(
        (t) => t.consultation_payment_status === "PAID"
      ).length,
    },
    {
      label: "Package Patients",
      color: "bg-purple-100 text-purple-800",
      count: packageToday.length,
    },
    {
      label: "Treatment Patients",
      color: "bg-indigo-100 text-indigo-800",
      count: treatmentsToday.length,
    },
    {
      label: "Unpaid Patients",
      color: "bg-red-100 text-red-800",
      count: unpaidTodayCount,
    },
  ];

  // =========================
  // UI (UNCHANGED STYLE)
  // =========================
  return (
    <div className="space-y-6">
      {/* TOP CARDS */}
      <div className="grid grid-cols-2 gap-4">
        {tokenStats.slice(0, 2).map((s) => (
          <div key={s.label} className={`py-5 rounded-xl ${s.color} text-center`}>
            <p>{s.label}</p>
            <p className="text-xl font-bold">{s.count}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {tokenStats.slice(2).map((s) => (
          <div key={s.label} className={`py-4 rounded-xl ${s.color} text-center`}>
            <p>{s.label}</p>
            <p className="text-xl font-bold">{s.count}</p>
          </div>
        ))}
      </div>

      {/* BRANCH */}
      {/* BRANCH */}
<div>
  <h3 className="text-lg font-semibold mb-3">
    Branch-wise Today Tokens
  </h3>

  <div className="grid md:grid-cols-2 gap-4">
    {branchStats.map((b) => (
      <div
        key={b.branch}
        className="bg-white border rounded-xl p-5 shadow-sm"
      >
        <h4 className="font-bold mb-3">{b.branch}</h4>

        <div className="grid grid-cols-2 gap-3 text-sm">
          
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <p>Total Tokens</p>
            <p className="font-bold text-lg">{b.total}</p>
          </div>

          <div className="bg-green-50 p-3 rounded-lg text-center">
            <p>Paid</p>
            <p className="font-bold text-lg">{b.paid}</p>
          </div>

          <div className="bg-red-50 p-3 rounded-lg text-center">
            <p>Unpaid</p>
            <p className="font-bold text-lg">{b.unpaid}</p>
          </div>

          <div className="bg-purple-50 p-3 rounded-lg text-center">
            <p>Packages</p>
            <p className="font-bold text-lg">{b.package}</p>
          </div>

          <div className="bg-indigo-50 p-3 rounded-lg text-center">
            <p>Treatments</p>
            <p className="font-bold text-lg">{b.treatmentCount}</p>
          </div>

          <div className="col-span-2 bg-yellow-50 p-4 rounded-lg text-center">
            <p className="text-sm">Revenue</p>
            <p className="font-bold text-xl">
              ₹ {(b.amount || 0) +
                (b.treatmentAmount || 0) +
                (b.packageAmount || 0)}
            </p>
          </div>

        </div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}